import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const deriveEffectiveRole = (userData, event) => {
  if (event?.viewer_role === 'manager') return 'event_manager';
  if (event?.viewer_role === 'staff') return 'team_member';
  return event?.viewer_role || userData?.role;
};

const pickInitialEvent = (events, savedEventId) => {
  if (!Array.isArray(events) || events.length === 0) return null;
  return savedEventId
    ? events.find((event) => event.id === savedEventId) || events[0]
    : events[0];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [isEventGuestAdmin, setIsEventGuestAdmin] = useState(false);
  const [adminAccessLoading, setAdminAccessLoading] = useState(false);
  const [aiSubscription, setAiSubscription] = useState(null);
  const [aiSubscriptionLoading, setAiSubscriptionLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      if (!userData?.is_active) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('current_event_id');
        setUser(null);
        setCurrentEvent(null);
        return;
      }
      setUser(userData);
      
      // Load the first accessible event, preserving the saved selection when possible.
      const savedEventId = localStorage.getItem('current_event_id');
      
      try {
        const eventsResponse = await api.get('/events');
        const eventToLoad = pickInitialEvent(eventsResponse.data, savedEventId);
        if (eventToLoad) {
          setCurrentEvent(eventToLoad);
          localStorage.setItem('current_event_id', eventToLoad.id);
        } else {
          setCurrentEvent(null);
          localStorage.removeItem('current_event_id');
        }
      } catch (err) {
        console.error('Failed to load events:', err);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('current_event_id');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const refreshAiSubscription = useCallback(async () => {
    if (!user?.id) {
      setAiSubscription(null);
      setAiSubscriptionLoading(false);
      return null;
    }

    setAiSubscriptionLoading(true);
    try {
      const response = await api.get('/auth/ai-subscription');
      setAiSubscription(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to check AI subscription:', error);
      setAiSubscription(null);
      return null;
    } finally {
      setAiSubscriptionLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshAiSubscription();
  }, [refreshAiSubscription]);

  useEffect(() => {
    const checkGuestAdminAccess = async () => {
      if (!user || !currentEvent?.id || user.role !== 'guest') {
        setIsEventGuestAdmin(false);
        setAdminAccessLoading(false);
        return;
      }

      try {
        setAdminAccessLoading(true);
        const response = await api.get(`/events/${currentEvent.id}/guests`);
        const myGuestProfile = (response.data || []).find((g) => g.user_id === user.id);
        setIsEventGuestAdmin(Boolean(myGuestProfile?.is_admin));
      } catch (error) {
        console.error('Failed to check guest admin access:', error);
        setIsEventGuestAdmin(false);
      } finally {
        setAdminAccessLoading(false);
      }
    };

    checkGuestAdminAccess();
  }, [user?.id, user?.role, currentEvent?.id]);

  const login = async (phone, password, { inviteToken = null, eventId = null } = {}) => {
    const response = await api.post('/auth/login', {
      phone,
      password,
      invite_token: inviteToken || null,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    let loadedEvent = null;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    try {
      const eventsResponse = await api.get('/events');
      loadedEvent = pickInitialEvent(
        eventsResponse.data,
        eventId || localStorage.getItem('current_event_id'),
      );
      if (loadedEvent) {
        setCurrentEvent(loadedEvent);
        localStorage.setItem('current_event_id', loadedEvent.id);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    }
    setUser(userData);

    return {
      ...userData,
      effective_role: deriveEffectiveRole(userData, loadedEvent),
    };
  };

  const loginWithOtp = async (
    phone,
    otpCode,
    { inviteToken = null, eventId = null, otpAccessToken = null } = {},
  ) => {
    const response = await api.post('/auth/login-otp', {
      phone,
      otp_code: otpCode || null,
      otp_access_token: otpAccessToken || null,
      invite_token: inviteToken || null,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    let loadedEvent = null;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    try {
      const eventsResponse = await api.get('/events');
      loadedEvent = pickInitialEvent(
        eventsResponse.data,
        eventId || localStorage.getItem('current_event_id'),
      );
      if (loadedEvent) {
        setCurrentEvent(loadedEvent);
        localStorage.setItem('current_event_id', loadedEvent.id);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    }
    setUser(userData);

    return {
      ...userData,
      effective_role: deriveEffectiveRole(userData, loadedEvent),
    };
  };

  const completeInviteOtp = async (
    inviteToken,
    otpCode,
    eventId,
    { otpAccessToken = null } = {},
  ) => {
    const response = await api.post('/auth/invite-otp/complete', {
      invite_token: inviteToken,
      otp_code: otpCode || null,
      otp_access_token: otpAccessToken || null,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    let loadedEvent = null;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);

    try {
      const eventsResponse = await api.get('/events');
      loadedEvent = pickInitialEvent(eventsResponse.data, eventId || localStorage.getItem('current_event_id'));
      if (loadedEvent) {
        setCurrentEvent(loadedEvent);
        localStorage.setItem('current_event_id', loadedEvent.id);
      }
    } catch (err) {
      console.error('Failed to load invited event:', err);
    }

    return {
      ...userData,
      effective_role: deriveEffectiveRole(userData, loadedEvent),
    };
  };

  const register = async ({ name, phone, password, email, inviteCode, inviteToken, eventId, otpVerifiedToken }) => {
    const response = await api.post('/auth/register', {
      name,
      email: email || null,
      password,
      phone,
      invite_code: inviteCode || null,
      invite_token: inviteToken || null,
      otp_verified_token: otpVerifiedToken || null,
    });

    const { access_token, refresh_token, user: userData } = response.data;
    let loadedEvent = null;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    if (!userData.has_password) {
      try {
        sessionStorage.setItem('galago_password_prompt_user', userData.id);
      } catch (_error) {
        // The prompt can still fall back to its periodic reminder when storage is unavailable.
      }
    }

    try {
      const eventsResponse = await api.get('/events');
      loadedEvent = pickInitialEvent(
        eventsResponse.data,
        eventId || localStorage.getItem('current_event_id'),
      );
      if (loadedEvent) {
        setCurrentEvent(loadedEvent);
        localStorage.setItem('current_event_id', loadedEvent.id);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    }
    setUser(userData);

    return {
      ...userData,
      effective_role: deriveEffectiveRole(userData, loadedEvent),
    };
  };


  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_event_id');
    setUser(null);
    setCurrentEvent(null);
    setAiSubscription(null);
    setAiSubscriptionLoading(false);
  };

  const switchEvent = useCallback(async (eventId) => {
    try {
      const eventResponse = await api.get(`/events/${eventId}`);
      setCurrentEvent(eventResponse.data);
      localStorage.setItem('current_event_id', eventId);
      return eventResponse.data;
    } catch (error) {
      console.error('Failed to switch event:', error);
      return null;
    }
  }, []);

  const claimInvite = async (inviteToken) => {
    const response = await api.post('/invites/claim', { invite_token: inviteToken });
    let loadedEvent = null;
    let userData = user;

    try {
      const userResponse = await api.get('/auth/me');
      userData = userResponse.data;
      setUser(userData);
    } catch (err) {
      console.error('Failed to refresh user after invite claim:', err);
    }

    try {
      const eventsResponse = await api.get('/events');
      loadedEvent = eventsResponse.data.find((event) => event.id === response.data.event_id)
        || pickInitialEvent(eventsResponse.data, response.data.event_id);
      if (loadedEvent) {
        setCurrentEvent(loadedEvent);
        localStorage.setItem('current_event_id', loadedEvent.id);
      }
    } catch (err) {
      console.error('Failed to load events after invite claim:', err);
    }

    return {
      ...response.data,
      effective_role: deriveEffectiveRole(userData, loadedEvent),
    };
  };

  const refreshCurrentEvent = useCallback(async () => {
    const eventId = currentEvent?.id || localStorage.getItem('current_event_id');
    if (!eventId) return null;

    try {
      const eventResponse = await api.get(`/events/${eventId}`);
      setCurrentEvent(eventResponse.data);
      localStorage.setItem('current_event_id', eventId);
      return eventResponse.data;
    } catch (error) {
      console.error('Failed to refresh current event:', error);
      return null;
    }
  }, [currentEvent?.id]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      if (!response.data?.is_active) {
        logout();
        return;
      }
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const currentEventRole = currentEvent?.viewer_role || null;
  const clearCurrentEvent = useCallback(() => {
    localStorage.removeItem('current_event_id');
    setCurrentEvent(null);
  }, []);
  const effectiveRole = currentEventRole === 'manager'
    ? 'event_manager'
    : currentEventRole === 'staff'
      ? 'team_member'
      : currentEventRole || user?.role;
  const isEventManager = effectiveRole === 'event_manager';
  const isTeamMember = effectiveRole === 'team_member';
  const isHotelManager = effectiveRole === 'hotel_manager';
  const isBride = effectiveRole === 'bride';
  const isGroom = effectiveRole === 'groom';
  const isHost = effectiveRole === 'host';
  const isCouple = isBride || isGroom;
  const isEventHost = isCouple || isHost;
  const isAdminGuest = effectiveRole === 'admin_guest';
  const isGuest = effectiveRole === 'guest';
  const canCreateEvents = !['team_member', 'hotel_manager'].includes(user?.role);
  const canJoinEvents = Boolean(user);
  const hasAdminAccess = isEventManager || isEventHost || isAdminGuest || isHotelManager || isEventGuestAdmin;
  const hasAiPremiumAccess = Boolean(aiSubscription?.is_unlimited);

  const value = {
    user,
    loading,
    currentEvent,
    clearCurrentEvent,
    login,
    loginWithOtp,
    completeInviteOtp,
    register,
    logout,
    switchEvent,
    claimInvite,
    refreshCurrentEvent,
    loadUser,
    refreshUser,
    isEventManager,
    isTeamMember,
    isHotelManager,
    isBride,
    isGroom,
    isHost,
    isCouple,
    isEventHost,
    isAdminGuest,
    isGuest,
    currentEventRole,
    effectiveRole,
    canCreateEvents,
    canJoinEvents,
    isEventGuestAdmin,
    adminAccessLoading,
    hasAdminAccess,
    aiSubscription,
    aiSubscriptionLoading,
    hasAiPremiumAccess,
    refreshAiSubscription
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
